import { Card, Table, Button, Tag, Space, Typography, notification, Image } from "antd";
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PiEyeDuotone } from "react-icons/pi";
import AppContext from "../../store/app.context";
import { requestGetProductList } from "../../services/admin.service";
import SearchInput from "../../components/SearchInput";

const { Title } = Typography;

function ProductsListPage() {
	const context = useContext(AppContext);
	const [searchParams, setSearchParams] = useSearchParams();
	const [loading, setLoading] = useState(false);
	const [products, setProducts] = useState([]);

	// Initialize state from URL parameters
	const [pagination, setPagination] = useState({
		current: parseInt(searchParams.get("page")) || 1,
		pageSize: parseInt(searchParams.get("limit")) || 10,
		total: 0,
	});
	const [filters, setFilters] = useState({
		keyword: searchParams.get("keyword") || "",
	});

	useEffect(() => {
		document.title = "Shopiva Console | Products Management";
		fetchProducts();
	}, []);

	useEffect(() => {
		fetchProducts();
	}, [pagination.current, pagination.pageSize, filters]);

	// Update URL parameters when state changes
	useEffect(() => {
		const params = new URLSearchParams();

		// Add pagination parameters
		if (pagination.current > 1) params.set("page", pagination.current.toString());
		if (pagination.pageSize !== 10) params.set("limit", pagination.pageSize.toString());

		// Add filter parameters
		if (filters.keyword) params.set("keyword", filters.keyword);

		// Update URL without triggering navigation
		setSearchParams(params, { replace: true });
	}, [pagination.current, pagination.pageSize, filters, setSearchParams]);

	const fetchProducts = () => {
		setLoading(true);

		// Build filter string from filter object
		const filterParts = [];
		// Add filter fields here as needed
		// Example: if (filters.status) filterParts.push(`status:${filters.status}`);
		// Example: if (filters.isActive) filterParts.push(`isActive:${filters.isActive}`);

		const filterString = filterParts.join(",");

		const params = {
			page: pagination.current,
			limit: pagination.pageSize,
			keyword: filters.keyword,
			filter: filterString,
			order: "_id:desc",
		};

		requestGetProductList(params)
			.then((response) => {
				// Server response structure: { code, message, pagination, data }
				const productsData = response.data.data || [];
				const serverPagination = response.data.pagination || {};

				setProducts(productsData);
				setPagination({
					...pagination,
					total: serverPagination.total || 0,
				});
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: "Failed to fetch products",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleSearch = (value) => {
		setFilters({ ...filters, keyword: value });
		setPagination({ ...pagination, current: 1 });
	};

	const handleTableChange = (paginationConfig) => {
		setPagination({
			...pagination,
			current: paginationConfig.current,
			pageSize: paginationConfig.pageSize,
		});
	};

	const getDefaultVariant = (variants) => {
		return variants?.find((v) => v.isDefault) || variants?.[0];
	};

	const formatCurrency = (amount) => {
		return new Intl.NumberFormat("id-ID", {
			style: "currency",
			currency: "IDR",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount);
	};

	const columns = [
		{
			title: "Image",
			dataIndex: "images",
			key: "image",
			width: 80,
			render: (images, record) => {
				const defaultVariant = getDefaultVariant(record.variants);
				const variantImages = defaultVariant?.images || [];
				const imageUrl = variantImages[0]?.url || images[0]?.url;

				return imageUrl ? (
					<Image
						src={imageUrl}
						alt={record.name}
						width={50}
						height={50}
						style={{ objectFit: "cover", borderRadius: 4 }}
					/>
				) : (
					<div
						style={{
							width: 50,
							height: 50,
							background: "#f0f0f0",
							borderRadius: 4,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							fontSize: 10,
							color: "#999",
						}}>
						No Image
					</div>
				);
			},
		},
		{
			title: "Product Name",
			dataIndex: "name",
			key: "name",
			render: (text, record) => (
				<div>
					<div style={{ fontWeight: 500 }}>{text}</div>
					<div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{record.slug}</div>
				</div>
			),
		},
		{
			title: "Price",
			dataIndex: "variants",
			key: "price",
			width: 150,
			align: "right",
			render: (variants) => {
				const defaultVariant = getDefaultVariant(variants);
				return defaultVariant ? (
					<div>
						<div style={{ fontWeight: 500 }}>{formatCurrency(defaultVariant.price)}</div>
						{variants.length > 1 && (
							<div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
								{variants.length} variants
							</div>
						)}
					</div>
				) : (
					"-"
				);
			},
		},
		{
			title: "Stock",
			dataIndex: "variants",
			key: "stock",
			width: 100,
			align: "center",
			render: (variants) => {
				const totalStock = variants?.reduce((sum, v) => sum + (v.stock || 0), 0) || 0;
				return <Tag color={totalStock > 0 ? "green" : "red"}>{totalStock}</Tag>;
			},
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			align: "center",
			width: 100,
			render: (status) => {
				const statusColors = {
					APPROVED: "green",
					PENDING: "orange",
					REJECTED: "red",
				};
				return <Tag color={statusColors[status] || "default"}>{status || "Unknown"}</Tag>;
			},
		},
		{
			title: "Active",
			dataIndex: "isActive",
			key: "isActive",
			align: "center",
			width: 80,
			render: (isActive) => (
				<Tag color={isActive ? "blue" : "default"}>{isActive ? "Yes" : "No"}</Tag>
			),
		},
		{
			title: "Created",
			dataIndex: "createdAt",
			key: "createdAt",
			align: "center",
			width: 120,
			render: (date) => new Date(date).toLocaleDateString(),
		},
		{
			title: "Actions",
			key: "actions",
			align: "right",
			width: 80,
			render: (_, record) => (
				<Space size="small">
					<Button type="text" icon={<PiEyeDuotone />} title="View Details" />
				</Space>
			),
		},
	];

	return (
		<div>
			<div style={{ marginBottom: 24 }}>
				<Title level={3}>Products Management</Title>
			</div>

			{/* Products Table */}
			<Card title="Product List">
				<div style={{ marginBottom: 16 }}>
					<SearchInput
						placeholder="Search products by name or slug..."
						onSearch={handleSearch}
						defaultValue={filters.keyword}
					/>
				</div>
				<Table
					columns={columns}
					dataSource={products}
					rowKey="_id"
					loading={loading}
					pagination={{
						current: pagination.current,
						pageSize: pagination.pageSize,
						total: pagination.total,
						showSizeChanger: true,
						showQuickJumper: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} products`,
					}}
					onChange={handleTableChange}
					scroll={{ x: 1200 }}
				/>
			</Card>
		</div>
	);
}

export default ProductsListPage;
